import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getApiBaseUrl } from '../../utils/apiUtils';
import { 
  ShoppingBag, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  DollarSign,
  Star,
  Download,
  Calendar,
  Package,
  TrendingUp,
  Loader2,
  AlertCircle,
  X,
  Play,
  CheckSquare,
  Square,
  Settings,
  Zap
} from 'lucide-react';
import { productAPI } from '../../services/api';
import api from '../../services/api';
import { getImageUrl, getS3ImageUrl } from '../../utils/apiUtils';
import DualEditor from './DualEditor';
import FileUpload from '../Upload/FileUpload'; // Added import for FileUpload

// Helper function to get category display name
const getCategoryDisplayName = (categorySlug, productCategories) => {
  const category = productCategories.find(cat => cat.slug === categorySlug);
  return category ? category.name : (categorySlug || 'Uncategorized');
};

const ProductsManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDefaultsModal, setShowDefaultsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Bulk actions state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    totalRevenue: 0,
    totalSales: 0,
    totalDownloads: 0
  });

  // Default values state
  const [defaultValues, setDefaultValues] = useState({
    rating: 0,
    reviews_count: 0,
    sales_count: 0,
    regular_license_points: [],
    regular_license_price: '',
    regular_license_original_price: '',
    regular_license_discount: 0,
    extended_license_points: [],
    extended_license_price: '',
    extended_license_original_price: '',
    extended_license_discount: 0,
    extended_support_text: '',
    extended_support_price: '',
    extended_support_original_price: '',
    extended_support_discount: 0,
    last_date: '',
    included: ''
  });
  const [defaultsLoading, setDefaultsLoading] = useState(false);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/product-categories?status=active`);
      const data = await response.json();
      if (data.success) {
        setProductCategories(data.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch product statistics
  const fetchStats = async () => {
    try {
      const response = await productAPI.getStats();
      if (response?.data?.success) {
        const statsData = response.data.data.stats;
        setStats({
          total: statsData.total || 0,
          totalRevenue: statsData.totalRevenue || 0,
          totalSales: statsData.bestSelling?.reduce((sum, product) => sum + (product.sales || 0), 0) || 0,
          totalDownloads: statsData.totalDownloads || 0
        });
      }
    } catch (error) {
      console.error('Error fetching product stats:', error);
    }
  };

  // Fetch default values
  const fetchDefaultValues = async () => {
    try {
      setDefaultsLoading(true);
      const response = await api.get('/product-defaults');
      if (response.data.success) {
        const data = response.data.data;
        // Parse JSON fields that come as strings from the database
        const parsedData = {
          ...data,
          regular_license_points: Array.isArray(data.regular_license_points) 
            ? data.regular_license_points 
            : (typeof data.regular_license_points === 'string' ? JSON.parse(data.regular_license_points || '[]') : []),
          extended_license_points: Array.isArray(data.extended_license_points) 
            ? data.extended_license_points 
            : (typeof data.extended_license_points === 'string' ? JSON.parse(data.extended_license_points || '[]') : []),
          // Normalize last_date to YYYY-MM-DD for date input compatibility
          last_date: data.last_date
            ? (typeof data.last_date === 'string'
                ? data.last_date.substring(0, 10)
                : (data.last_date instanceof Date
                    ? data.last_date.toISOString().split('T')[0]
                    : '')
              )
            : ''
        };
        setDefaultValues(parsedData);
      }
    } catch (error) {
      console.error('Error fetching default values:', error);
    } finally {
      setDefaultsLoading(false);
    }
  };

  // Save default values
  const saveDefaultValues = async () => {
    try {
      setDefaultsLoading(true);
      // Ensure arrays are properly formatted for the backend
      const dataToSave = {
        ...defaultValues,
        regular_license_points: Array.isArray(defaultValues.regular_license_points) 
          ? defaultValues.regular_license_points 
          : [],
        extended_license_points: Array.isArray(defaultValues.extended_license_points) 
          ? defaultValues.extended_license_points 
          : [],
        // Save last_date as the raw YYYY-MM-DD string (avoid timezone shifts)
        last_date: defaultValues.last_date || null
      };
      const response = await api.put('/product-defaults', dataToSave);
      if (response.data.success) {
        alert('Default values saved successfully!');
        setShowDefaultsModal(false);
      } else {
        alert('Failed to save default values');
      }
    } catch (error) {
      console.error('Error saving default values:', error);
      alert('Error saving default values');
    } finally {
      setDefaultsLoading(false);
    }
  };

  // Fetch products from API
  const fetchProducts = async (page = currentPage, limit = pageSize) => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters
      const params = {
        status: 'all',
        page: page,
        limit: limit
      };
      
      // Add filters if they're not 'all'
      if (filterCategory !== 'all') {
        params.category = filterCategory;
      }
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      const response = await productAPI.getAll(params);
      
      if (response?.data?.success) {
        const productsData = response.data.data.products || [];
        const paginationData = response.data.data.pagination || {};
        
        // Parse JSON fields and format data
        const formattedProducts = productsData.map(product => {
          try {
            return {
              ...product,
              tags: typeof product.tags === 'string' ? JSON.parse(product.tags) : product.tags || [],
              gallery: typeof product.gallery === 'string' ? JSON.parse(product.gallery) : product.gallery || [],
              features: typeof product.features === 'string' ? JSON.parse(product.features) : product.features || [],
              featured: Boolean(product.featured),
              // Compute display price like website products page (prefer regular, fallback to extended)
              price: (product.regular_license_price != null && product.regular_license_price !== '')
                ? Number(product.regular_license_price)
                : Number(product.extended_license_price || 0),
              original_price: (product.regular_license_original_price != null && product.regular_license_original_price !== '')
                ? Number(product.regular_license_original_price)
                : (product.extended_license_original_price != null && product.extended_license_original_price !== '')
                  ? Number(product.extended_license_original_price)
                  : null,
              // Format dates
              last_updated: product.updated_at || product.created_at
            };
          } catch (parseError) {
            console.error('Error parsing product:', product, parseError);
            return {
              ...product,
              tags: [],
              gallery: [],
              features: [],
              featured: Boolean(product.featured),
              price: Number(product.regular_license_price || product.extended_license_price || 0),
              original_price: product.regular_license_original_price || product.extended_license_original_price || null,
              last_updated: product.updated_at || product.created_at
            };
          }
        });
        
        setProducts(formattedProducts);
        setCurrentPage(paginationData.page || 1);
        setTotalPages(paginationData.pages || 1);
        setTotalProducts(paginationData.total || 0);
      } else {
        console.error('API response not successful:', response);
        setError('Failed to fetch products');
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(`Error loading products: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchProducts(newPage, pageSize);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    fetchProducts(1, newPageSize);
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(1, pageSize);
  }, [filterCategory, filterStatus, searchTerm]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchStats();
    fetchDefaultValues();
  }, []);

  const handleAddProduct = async (formData) => {
    try {
      setLoading(true);
      
      // Prepare data for API - don't double-encode JSON fields
      const productData = {
        ...formData,
        // Keep arrays as arrays, backend will handle JSON encoding
        tags: formData.tags || [],
        gallery: formData.gallery || [],
        features: formData.features || [],
        status: formData.status || 'active',
        featured: Boolean(formData.featured),
        // Fix date format for last_date
        last_date: formData.last_date ? new Date(formData.last_date).toISOString().split('T')[0] : null
      };
      
      console.log('Sending product data:', productData);
      console.log('Category being sent:', productData.category);
      
      const response = await productAPI.create(productData);
      
      if (response?.data?.success) {
        // Refresh products list
        await fetchProducts();
        setShowAddModal(false);
      } else {
        setError('Failed to add product');
      }
    } catch (err) {
      console.error('Error adding product:', err);
      setError(`Error adding product: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProduct = async (formData) => {
    try {
      setLoading(true);
      
      // Prepare data for API - don't double-encode JSON fields
      const productData = {
        ...formData,
        // Keep arrays as arrays, backend will handle JSON encoding
        tags: formData.tags || [],
        gallery: formData.gallery || [],
        features: formData.features || [],
        status: formData.status || 'active',
        featured: Boolean(formData.featured),
        // Fix date format for last_date
        last_date: formData.last_date ? new Date(formData.last_date).toISOString().split('T')[0] : null
      };
      
      console.log('Sending product data:', productData);
      console.log('Category being sent:', productData.category);
      
      const response = await productAPI.update(selectedProduct.id, productData);
      
      if (response?.data?.success) {
        // Refresh products list
        await fetchProducts();
        setShowEditModal(false);
        setSelectedProduct(null);
      } else {
        setError('Failed to update product');
      }
    } catch (err) {
      console.error('Error updating product:', err);
      setError(`Error updating product: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        setLoading(true);
        
        const response = await productAPI.delete(productId);
        
        if (response?.data?.success) {
          // Refresh products list
          await fetchProducts();
        } else {
          setError('Failed to delete product');
        }
      } catch (err) {
        console.error('Error deleting product:', err);
        setError(`Error deleting product: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  // Bulk action functions
  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts([]);
      setSelectAll(false);
    } else {
      setSelectedProducts(filteredProducts.map(product => product.id));
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (!selectedProducts.length) return;
    
    if (!window.confirm(`Are you sure you want to update ${selectedProducts.length} product(s) status to ${status}?`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await productAPI.bulkUpdateStatus(selectedProducts, status);
      
      if (response?.data?.success) {
        await fetchProducts();
        setSelectedProducts([]);
        setSelectAll(false);
        setShowBulkActions(false);
      } else {
        setError('Failed to update product status');
      }
    } catch (err) {
      console.error('Error updating product status:', err);
      setError(`Error updating product status: ${err.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkFeaturedUpdate = async (featured) => {
    if (!selectedProducts.length) return;
    
    if (!window.confirm(`Are you sure you want to ${featured ? 'set' : 'remove'} featured status for ${selectedProducts.length} product(s)?`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await productAPI.bulkUpdateFeatured(selectedProducts, featured);
      
      if (response?.data?.success) {
        await fetchProducts();
        setSelectedProducts([]);
        setSelectAll(false);
        setShowBulkActions(false);
      } else {
        setError('Failed to update featured status');
      }
    } catch (err) {
      console.error('Error updating featured status:', err);
      setError(`Error updating featured status: ${err.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedProducts.length) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedProducts.length} product(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await productAPI.bulkDelete(selectedProducts);
      
      if (response?.data?.success) {
        await fetchProducts();
        setSelectedProducts([]);
        setSelectAll(false);
        setShowBulkActions(false);
      } else {
        setError('Failed to delete products');
      }
    } catch (err) {
      console.error('Error deleting products:', err);
      setError(`Error deleting products: ${err.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Update showBulkActions when selectedProducts changes
  useEffect(() => {
    setShowBulkActions(selectedProducts.length > 0);
  }, [selectedProducts]);

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'active':
        return `${baseClasses} text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/20`;
      case 'draft':
        return `${baseClasses} text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20`;
      case 'coming_soon':
        return `${baseClasses} text-blue-700 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20`;
      case 'inactive':
        return `${baseClasses} text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20`;
      default:
        return `${baseClasses} text-gray-700 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20`;
    }
  };

  // Products are now filtered server-side, so we use them directly
  const filteredProducts = products;

  // Use stats from API instead of calculating from products array

  // Get categories for dropdown (only from productCategories list)
  const categories = [
    { value: 'all', label: 'All Categories' },
    ...productCategories.map(cat => ({ value: cat.slug, label: cat.name }))
  ];

  if (loading && products.length === 0) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <span className="ml-2 text-gray-600">Loading products...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <AlertCircle className="w-8 h-8 text-red-600" />
          <span className="ml-2 text-red-600">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <ShoppingBag className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              Products Management
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
              Manage digital products, themes, templates, and plugins
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center touch-manipulation"
            >
              <Plus className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Add Product</span>
              <span className="sm:hidden">Add</span>
            </button>
            <button
              onClick={() => setShowDefaultsModal(true)}
              className="bg-gray-600 hover:bg-gray-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center touch-manipulation"
            >
              <Settings className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Default Values</span>
              <span className="sm:hidden">Defaults</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-4 sm:p-6 mb-4 sm:mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <ShoppingBag className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Products</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <DollarSign className="h-4 w-4 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                ₹{stats.totalRevenue.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
              <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Sales</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSales}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
              <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Sales</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {stats.totalDownloads.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full sm:w-48 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            {categories.map(category => (
              <option key={category.value} value={category.value}>{category.label}</option>
            ))}
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full sm:w-48 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="coming_soon">Coming Soon</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedProducts.length} product(s) selected
              </span>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkStatusUpdate('active')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  Set Active
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('inactive')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  Set Inactive
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('coming_soon')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  Set Coming Soon
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('draft')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  Set Draft
                </button>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkFeaturedUpdate(true)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Star className="w-3 h-3 mr-1" />
                  Set Featured
                </button>
                
                <button
                  onClick={() => handleBulkFeaturedUpdate(false)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Star className="w-3 h-3 mr-1" />
                  Remove Featured
                </button>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
                className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Delete Selected
              </button>
              
              <button
                onClick={() => {
                  setSelectedProducts([]);
                  setSelectAll(false);
                  setShowBulkActions(false);
                }}
                className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                <X className="w-3 h-3 mr-1" />
                Clear Selection
              </button>
            </div>
          </div>
          
          {bulkActionLoading && (
            <div className="mt-3 flex items-center text-blue-700 dark:text-blue-300">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              <span className="text-sm">Processing bulk action...</span>
            </div>
          )}
        </div>
      )}

      {/* Products Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  <div className="flex items-center">
                    <button
                      onClick={handleSelectAll}
                      className="mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {selectAll ? (
                        <CheckSquare className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </button>
                    Product
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Performance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredProducts.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <button
                        onClick={() => handleSelectProduct(product.id)}
                        className="mr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {selectedProducts.includes(product.id) ? (
                          <CheckSquare className="h-4 w-4 text-blue-600" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </button>
                      <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                        <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                          {product.name}
                          {product.featured && (
                            <Star className="h-4 w-4 text-yellow-500 ml-2" />
                          )}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          v{product.version}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{getCategoryDisplayName(product.category, productCategories)}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {product.author || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {product.original_price ? (
                        <div>
                          <span className="line-through text-gray-500">₹{product.original_price}</span>
                          <span className="ml-2 text-green-600 font-medium">₹{product.price}</span>
                        </div>
                      ) : (
                        <span>₹{product.price}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(product.status)}>
                      {product.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="space-y-1">
                      <div className="flex items-center">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        {product.sales_count || product.downloads || 0} sales
                      </div>
                      <div className="flex items-center">
                        <Star className="h-3 w-3 mr-1 text-yellow-500" />
                        {product.rating > 0 ? `${product.rating} (${product.reviews_count || 0})` : 'No reviews'}
                      </div>
                      <div className="flex items-center">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        ₹{(product.price || 0).toLocaleString()} price
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(product.last_updated).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => {
                          setSelectedProduct(product);
                          setShowEditModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, totalProducts)}
                </span>{' '}
                of <span className="font-medium">{totalProducts}</span> results
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="page-size" className="text-sm text-gray-700 dark:text-gray-300">
                  Show:
                </label>
                <select
                  id="page-size"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                </select>
              </div>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNum === currentPage
                          ? 'z-10 bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddModal && createPortal(
        <ProductModal
          key="add-product-modal"
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddProduct}
          title="Add Product"
          categories={categories}
          productCategories={productCategories}
          loading={loading}
          defaultValues={defaultValues}
        />
      , document.body)}

      {/* Edit Product Modal */}
      {showEditModal && selectedProduct && createPortal(
        <ProductModal
          key={`edit-product-modal-${selectedProduct.id}`}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProduct(null);
          }}
          onSubmit={handleEditProduct}
          title="Edit Product"
          product={selectedProduct}
          categories={categories}
          productCategories={productCategories}
          loading={loading}
        />
      , document.body)}

      {/* Default Values Modal */}
      {showDefaultsModal && createPortal(
        <DefaultValuesModal
          isOpen={showDefaultsModal}
          onClose={() => setShowDefaultsModal(false)}
          defaultValues={defaultValues}
          setDefaultValues={setDefaultValues}
          onSave={saveDefaultValues}
          loading={defaultsLoading}
        />
      , document.body)}
    </div>
  );
};

// Product Modal Component
const ProductModal = ({ isOpen, onClose, onSubmit, title, product = null, categories, productCategories, loading = false, defaultValues = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    slugError: '',
    description: '',
    full_description: '',
    category: 'themes',
    image: '',
    image_type: 'url',
    download_url: '',
    demo_url: '',
    video_url: '',
    documentation_url: '',
    version: '1.0.0',
    author: '',
    status: 'active',
    featured: false,
    disable_full_payment_coupon: false,
    tags: [],
    features: [],
    gallery: [],
    screenshots: [],
    // License fields
    regular_license_points: [],
    regular_license_price: '',
    regular_license_original_price: '',
    regular_license_discount: 0,
    extended_license_points: [],
    extended_license_price: '',
    extended_license_original_price: '',
    extended_license_discount: 0,
    // Support fields
    extended_support_text: '',
    extended_support_price: '',
    extended_support_original_price: '',
    extended_support_discount: 0,
    // Additional fields
    addons: [],
    last_date: '',
    included: '',
    // Rating and sales fields
    rating: 0,
    reviews_count: 0,
    sales_count: 0
  });

  // Product selector modal state
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');

  // Slug validation state
  const [slugValidationTimeout, setSlugValidationTimeout] = useState(null);

  // CodeCanyon integration state
  const [codecanyonUrl, setCodecanyonUrl] = useState('');
  const [codecanyonLoading, setCodecanyonLoading] = useState(false);
  const [codecanyonError, setCodecanyonError] = useState('');

  // Reset form when modal opens or product changes
  useEffect(() => {
    if (isOpen && product !== undefined) {
      // Reset CodeCanyon state
      setCodecanyonUrl('');
      setCodecanyonError('');
      setCodecanyonLoading(false);
      
      // Debug initial category
      const defaultCategory = product?.category || (productCategories.length > 0 ? productCategories[0].slug : 'themes');
      console.log('Modal opened - Initial category:', defaultCategory);
      console.log('Product categories available:', productCategories);
      console.log('Categories array:', categories);
      
      setFormData({
        name: product?.name || '',
        slug: product?.slug || '',
        slugError: '',
        description: product?.description || '',
        full_description: product?.full_description || '',
        category: product?.category || (productCategories.length > 0 ? productCategories[0].slug : 'themes'),
        image: product?.image || '',
        image_type: product?.image?.startsWith('data:') ? 'upload' : 'url',
        download_url: product?.download_url || '',
        demo_url: product?.demo_url || '',
        video_url: product?.video_url || '',
        documentation_url: product?.documentation_url || '',
        version: product?.version || '1.0.0',
        author: product?.author || '',
        status: product?.status || 'active',
        featured: product?.featured || false,
        disable_full_payment_coupon: Boolean(product?.disable_full_payment_coupon) || false,
        tags: product?.tags || [],
        features: product?.features || [],
        gallery: product?.gallery || [],
        screenshots: Array.isArray(product?.screenshots) ? 
          product.screenshots.map(screenshot => 
            typeof screenshot === 'string' ? { url: screenshot, type: 'url' } : screenshot
          ) : [],
        // License fields
        regular_license_points: Array.isArray(product?.regular_license_points) ? product.regular_license_points : 
          (typeof product?.regular_license_points === 'string' && product.regular_license_points) ? 
          product.regular_license_points.split('\n').filter(point => point.trim()) : 
          (defaultValues?.regular_license_points || []),
        regular_license_price: product?.regular_license_price || (defaultValues?.regular_license_price || ''),
        regular_license_original_price: product?.regular_license_original_price || (defaultValues?.regular_license_original_price || ''),
        regular_license_discount: product?.regular_license_discount || (defaultValues?.regular_license_discount || 0),
        extended_license_points: Array.isArray(product?.extended_license_points) ? product.extended_license_points : 
          (typeof product?.extended_license_points === 'string' && product.extended_license_points) ? 
          product.extended_license_points.split('\n').filter(point => point.trim()) : 
          (defaultValues?.extended_license_points || []),
        extended_license_price: product?.extended_license_price || (defaultValues?.extended_license_price || ''),
        extended_license_original_price: product?.extended_license_original_price || (defaultValues?.extended_license_original_price || ''),
        extended_license_discount: product?.extended_license_discount || (defaultValues?.extended_license_discount || 0),
        // Support fields
        extended_support_text: product?.extended_support_text || (defaultValues?.extended_support_text || ''),
        extended_support_price: product?.extended_support_price || (defaultValues?.extended_support_price || ''),
        extended_support_original_price: product?.extended_support_original_price || (defaultValues?.extended_support_original_price || ''),
        extended_support_discount: product?.extended_support_discount || (defaultValues?.extended_support_discount || 0),
        // Additional fields
        addons: product?.addons || [],
        last_date: product?.last_date ? new Date(product.last_date).toISOString().split('T')[0] : (defaultValues?.last_date || ''),
        included: product?.included || (defaultValues?.included || ''),
        // Rating and sales fields
        rating: product?.rating || (defaultValues?.rating || 0),
        reviews_count: product?.reviews_count || (defaultValues?.reviews_count || 0),
        sales_count: product?.sales_count || (defaultValues?.sales_count || 0)
      });
    }
  }, [isOpen, product, productCategories, defaultValues]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (slugValidationTimeout) {
        clearTimeout(slugValidationTimeout);
      }
    };
  }, [slugValidationTimeout]);

  // Fetch available products for addon selection
  const fetchAvailableProducts = async () => {
    try {
      const response = await productAPI.getAll({ status: 'all', limit: 1000 });
      if (response?.data?.success) {
        // Filter out the current product being edited
        const filteredProducts = response.data.data.products.filter(p => 
          !product || p.id !== product.id
        );
        setAvailableProducts(filteredProducts);
      }
    } catch (error) {
      console.error('Error fetching available products:', error);
    }
  };

  // Open product selector and fetch products
  const handleOpenProductSelector = () => {
    setShowProductSelector(true);
    fetchAvailableProducts();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check for slug errors
    if (formData.slugError) {
      alert('Please fix the slug error before submitting.');
      return;
    }
    
    // Safety checks for form data
    const safeFormData = {
      ...formData,
      name: formData.name || '',
      // Generate slug if not provided
      slug: formData.slug || (formData.name ? formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : '')
    };
    
    onSubmit(safeFormData);
  };

  // Helper to compute price/original/discount from any two
  const computePriceTriple = (priceInput, originalInput, discountInput) => {
    const price = priceInput === '' || priceInput === null || priceInput === undefined ? null : Number(priceInput);
    const original = originalInput === '' || originalInput === null || originalInput === undefined ? null : Number(originalInput);
    const discount = discountInput === '' || discountInput === null || discountInput === undefined ? null : Number(discountInput);

    let outPrice = price;
    let outOriginal = original;
    let outDiscount = discount;

    // If price missing but original and discount present
    if ((outPrice === null || isNaN(outPrice)) && outOriginal != null && !isNaN(outOriginal) && outDiscount != null && !isNaN(outDiscount)) {
      outPrice = +(outOriginal * (1 - outDiscount / 100)).toFixed(2);
    }

    // If discount missing but price and original present
    if ((outDiscount === null || isNaN(outDiscount)) && outPrice != null && !isNaN(outPrice) && outOriginal != null && !isNaN(outOriginal) && outOriginal > 0) {
      outDiscount = +(((outOriginal - outPrice) / outOriginal) * 100).toFixed(2);
    }

    // If original missing but price and discount present
    if ((outOriginal === null || isNaN(outOriginal)) && outPrice != null && !isNaN(outPrice) && outDiscount != null && !isNaN(outDiscount)) {
      const denom = (1 - outDiscount / 100);
      outOriginal = denom !== 0 ? +(outPrice / denom).toFixed(2) : outPrice;
    }

    return {
      price: outPrice ?? 0,
      original: outOriginal ?? 0,
      discount: outDiscount ?? 0,
    };
  };

  // Function to generate slug from name
  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Function to validate slug uniqueness
  const validateSlug = async (slug, currentProductId = null) => {
    if (!slug) return;
    
    try {
      const response = await productAPI.validateSlug(slug, currentProductId);
      
      if (!response.data.success || response.data.data.exists) {
        setFormData(prev => ({
          ...prev,
          slugError: 'Slug not available. Please choose a different one.'
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          slugError: ''
        }));
      }
    } catch (error) {
      console.error('Error validating slug:', error);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : (e.target.value || '');
    const name = e.target.name;
    
    // Debug category selection
    if (name === 'category') {
      console.log('Category changed:', value);
      console.log('Available categories:', categories);
      console.log('Product categories:', productCategories);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate slug when name changes
    if (name === 'name' && value) {
      const generatedSlug = generateSlug(value);
      setFormData(prev => ({
        ...prev,
        slug: generatedSlug,
        slugError: ''
      }));
    }

    // Validate slug when slug changes
    if (name === 'slug' && value) {
      // Clear previous timeout
      if (slugValidationTimeout) {
        clearTimeout(slugValidationTimeout);
      }

      // Set new timeout for debounced validation
      const timeout = setTimeout(() => {
        validateSlug(value, product?.id);
      }, 500);

      setSlugValidationTimeout(timeout);
    }

    // Auto-calc Regular License fields
    if (name === 'regular_license_price' || name === 'regular_license_original_price' || name === 'regular_license_discount') {
      setFormData(prev => {
        const { price, original, discount } = computePriceTriple(
          name === 'regular_license_price' ? value : prev.regular_license_price,
          name === 'regular_license_original_price' ? value : prev.regular_license_original_price,
          name === 'regular_license_discount' ? value : prev.regular_license_discount
        );
        return {
          ...prev,
          regular_license_price: String(price),
          regular_license_original_price: String(original),
          regular_license_discount: Number(discount),
        };
      });
    }

    // Auto-calc Extended License fields
    if (name === 'extended_license_price' || name === 'extended_license_original_price' || name === 'extended_license_discount') {
      setFormData(prev => {
        const { price, original, discount } = computePriceTriple(
          name === 'extended_license_price' ? value : prev.extended_license_price,
          name === 'extended_license_original_price' ? value : prev.extended_license_original_price,
          name === 'extended_license_discount' ? value : prev.extended_license_discount
        );
        return {
          ...prev,
          extended_license_price: String(price),
          extended_license_original_price: String(original),
          extended_license_discount: Number(discount),
        };
      });
    }

    // Auto-calc Extended Support fields
    if (name === 'extended_support_price' || name === 'extended_support_original_price' || name === 'extended_support_discount') {
      setFormData(prev => {
        const { price, original, discount } = computePriceTriple(
          name === 'extended_support_price' ? value : prev.extended_support_price,
          name === 'extended_support_original_price' ? value : prev.extended_support_original_price,
          name === 'extended_support_discount' ? value : prev.extended_support_discount
        );
        return {
          ...prev,
          extended_support_price: String(price),
          extended_support_original_price: String(original),
          extended_support_discount: Number(discount),
        };
      });
    }

    // Auto-calc Addons when addons array is updated
    if (name === 'addons') {
      const normalizedAddons = Array.isArray(value) ? value.map(a => {
        const { price, original, discount } = computePriceTriple(a.price, a.original_price, a.discount_percentage);
        return {
          ...a,
          price: String(price),
          original_price: String(original),
          discount_percentage: Number(discount),
        };
      }) : value;
      setFormData(prev => ({ ...prev, addons: normalizedAddons }));
    }
  };

  // Handle CodeCanyon data fetching
  const handleFetchFromCodeCanyon = async () => {
    if (!codecanyonUrl.trim()) {
      setCodecanyonError('Please enter a CodeCanyon URL');
      return;
    }

    setCodecanyonLoading(true);
    setCodecanyonError('🔄 Fetching product data from CodeCanyon... This may take 15-30 seconds.');

    try {
      const response = await productAPI.fetchFromCodeCanyon(codecanyonUrl);
      
      if (response.data.success) {
        const fetchedData = response.data.data;
        
        // Update form data with fetched information
        // Don't override category if it's 'other' or null from CodeCanyon mapping
        const codecanyonCategory = fetchedData.category;
        const shouldUseCodecanyonCategory = codecanyonCategory && codecanyonCategory !== 'other' && codecanyonCategory !== null;
        
        console.log('CodeCanyon category mapping:', {
          codecanyonCategory,
          shouldUseCodecanyonCategory,
          currentCategory: formData.category
        });
        
        setFormData(prev => ({
          ...prev,
          name: fetchedData.name || prev.name,
          slug: fetchedData.slug || prev.slug,
          description: fetchedData.description || prev.description,
          full_description: fetchedData.full_description || prev.full_description,
          category: shouldUseCodecanyonCategory ? codecanyonCategory : prev.category,
          image: fetchedData.image || prev.image,
          screenshots: fetchedData.screenshots || prev.screenshots,
          tags: fetchedData.tags || prev.tags,
          author: fetchedData.author || prev.author,
          version: fetchedData.version || prev.version,
          demo_url: fetchedData.demo_url || prev.demo_url,
          documentation_url: fetchedData.documentation_url || prev.documentation_url,
          regular_license_price: fetchedData.regular_license_price || prev.regular_license_price,
          features: fetchedData.features || prev.features,
        }));

        // Show success message
        setCodecanyonError('');
        
        // Clear the URL input
        setCodecanyonUrl('');
        
        // Show success notification with category info
        let successMessage = '✅ Product data fetched successfully! All fields have been populated.';
        if (!shouldUseCodecanyonCategory) {
          successMessage += ' Note: Category was not changed (keeping your selection or using default).';
        }
        
        setCodecanyonError(successMessage);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setCodecanyonError('');
        }, 5000);
        
      } else {
        setCodecanyonError(response.data.message || 'Failed to fetch product data');
      }
    } catch (error) {
      console.error('Error fetching CodeCanyon data:', error);
      setCodecanyonError(
        error.response?.data?.message || 
        'Failed to fetch product data from CodeCanyon. Please check the URL and try again.'
      );
    } finally {
      setCodecanyonLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {product ? 'Update product information and settings' : 'Create a new product with all details'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                form="product-form"
                disabled={loading}
                className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? 'Saving...' : product ? 'Update Product' : 'Create Product'}
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Modal Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
        
        <form id="product-form" onSubmit={handleSubmit} className="space-y-4">
          {/* CodeCanyon Integration Section */}
          <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-4 sm:p-6 border border-orange-200 dark:border-orange-800">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-600" />
              CodeCanyon Integration
            </h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  CodeCanyon Product URL
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={codecanyonUrl}
                    onChange={(e) => setCodecanyonUrl(e.target.value)}
                    placeholder="https://codecanyon.net/item/product-name/123456"
                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                  />
                  <button
                    type="button"
                    onClick={handleFetchFromCodeCanyon}
                    disabled={!codecanyonUrl || codecanyonLoading}
                    className="px-6 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    {codecanyonLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        Auto-Fill
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Paste a CodeCanyon product URL to automatically populate product details
                </p>
                {codecanyonError && (
                  <p className={`text-sm mt-1 ${codecanyonError.startsWith('✅') ? 'text-green-600' : 'text-red-500'}`}>
                    {codecanyonError}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Basic Information Section */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 sm:p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Basic Information
            </h4>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter product name..."
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Slug *
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  required
                  placeholder="product-slug-url"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors ${
                    formData.slug && formData.slugError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {formData.slug && formData.slugError && (
                  <p className="text-red-500 text-sm mt-1">{formData.slugError}</p>
                )}
                <p className="text-gray-500 text-sm mt-1">
                  URL-friendly version of the product name. Will be auto-generated from the title.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                >
                  {categories.filter(cat => cat.value !== 'all').map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Author
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  placeholder="Enter author name..."
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Version
                </label>
                <input
                  type="text"
                  name="version"
                  value={formData.version}
                  onChange={handleChange}
                  placeholder="1.0.0"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                />
              </div>
            </div>
            
            <div className="mt-6">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Short Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                placeholder="Enter a brief description of the product..."
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
              />
            </div>
            
            <div className="mt-6">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Full Description
              </label>
              <DualEditor
                value={formData.full_description || ''}
                onChange={(content) => {
                  setFormData(prev => ({
                    ...prev,
                    full_description: content || ''
                  }));
                }}
                placeholder="Enter full description with rich formatting..."
              />
            </div>
          </div>

          {/* Rating & Sales Section */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 sm:p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-600" />
              Rating & Sales Information
            </h4>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Product Rating
                </label>
                <input
                  type="number"
                  name="rating"
                  value={formData.rating}
                  onChange={handleChange}
                  min="0"
                  max="5"
                  step="0.1"
                  placeholder="4.5"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                />
                <p className="text-gray-500 text-sm mt-1">
                  Rating from 0.0 to 5.0
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Number of Reviews
                </label>
                <input
                  type="number"
                  name="reviews_count"
                  value={formData.reviews_count}
                  onChange={handleChange}
                  min="0"
                  placeholder="150"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                />
                <p className="text-gray-500 text-sm mt-1">
                  Total number of reviews
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Number of Sales
                </label>
                <input
                  type="number"
                  name="sales_count"
                  value={formData.sales_count}
                  onChange={handleChange}
                  min="0"
                  placeholder="500"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                />
                <p className="text-gray-500 text-sm mt-1">
                  Total number of sales
                </p>
              </div>
            </div>
          </div>

          {/* Media & URLs Section */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 sm:p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Play className="w-5 h-5 text-green-600" />
              Media & URLs
            </h4>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Product Image
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      handleChange({
                        target: {
                          name: 'image_type',
                          value: 'url'
                        }
                      });
                    }}
                    className={`text-xs px-3 py-1 rounded transition-colors ${
                      formData.image_type === 'url' 
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    URL
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleChange({
                        target: {
                          name: 'image_type',
                          value: 'upload'
                        }
                      });
                    }}
                    className={`text-xs px-3 py-1 rounded transition-colors ${
                      formData.image_type === 'upload' 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    Upload
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                
                {formData.image_type === 'upload' ? (
                  <div className="space-y-3">
                    {/* File Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Upload Image/Animation
                      </label>
                      <FileUpload
                        multiple={false}
                        accept="image/*,application/json,.gif,.json"
                        maxSize={10 * 1024 * 1024} // 10MB
                        maxFiles={1}
                        uploadType="product"
                        autoUpload={true}
                        onUploadComplete={(files) => {
                          if (files.length > 0) {
                            handleChange({
                              target: {
                                name: 'image',
                                value: files[0].url
                              }
                            });
                            handleChange({
                              target: {
                                name: 'image_type',
                                value: 'upload'
                              }
                            });
                          }
                        }}
                        onUploadError={(errors) => {
                          console.error('Upload errors:', errors);
                        }}
                        className="w-full"
                      />
                    </div>
                    
                    {/* Preview */}
                    {formData.image && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Preview
                        </label>
                        <div className="w-48 h-36 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center border border-gray-200 dark:border-gray-600">
                          {formData.image.toLowerCase().endsWith('.json') ? (
                            <div className="text-center">
                              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
                                <Play className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">Lottie Animation</p>
                            </div>
                          ) : formData.image.toLowerCase().endsWith('.gif') ? (
                            <img 
                              src={getS3ImageUrl(formData.image)} 
                              alt="Product preview" 
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <img 
                              src={getS3ImageUrl(formData.image)} 
                              alt="Product preview" 
                              className="w-full h-full object-contain"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <input
                      type="text"
                      name="image"
                      value={formData.image}
                      onChange={handleChange}
                      placeholder="https://example.com/image.jpg or https://lottiefiles.com/animations/product.json"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    
                    {/* Preview for URL */}
                    {formData.image && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Preview
                        </label>
                        <div className="w-48 h-36 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center border border-gray-200 dark:border-gray-600">
                          {formData.image.toLowerCase().endsWith('.json') ? (
                            <div className="text-center">
                              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
                                <Play className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">Lottie Animation</p>
                            </div>
                          ) : formData.image.toLowerCase().endsWith('.gif') ? (
                            <img 
                              src={getS3ImageUrl(formData.image)} 
                              alt="Product preview" 
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <img 
                              src={getS3ImageUrl(formData.image)} 
                              alt="Product preview" 
                              className="w-full h-full object-contain"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Download URL
              </label>
              <input
                type="url"
                name="download_url"
                value={formData.download_url}
                onChange={handleChange}
                placeholder="https://example.com/download"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Demo URL
              </label>
              <input
                type="url"
                name="demo_url"
                value={formData.demo_url}
                onChange={handleChange}
                placeholder="https://example.com/demo"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Video URL
              </label>
              <input
                type="url"
                name="video_url"
                value={formData.video_url}
                onChange={handleChange}
                placeholder="https://example.com/video"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Screenshots
              </label>
              <div className="space-y-3">
                {Array.isArray(formData.screenshots) ? formData.screenshots.map((screenshot, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Screenshot {index + 1}
                        </span>
                        <span className="text-xs text-gray-500">({screenshot.type || 'URL'})</span>
                      </div>
                      
                      {screenshot.type === 'upload' ? (
                        <div className="flex items-center gap-2">
                          <img 
                            src={getS3ImageUrl(screenshot.url)} 
                            alt={`Screenshot ${index + 1}`}
                            className="w-24 h-18 object-contain rounded border border-gray-200 dark:border-gray-600"
                          />
                          <div className="flex-1">
                            <FileUpload
                              multiple={false}
                              accept="image/*"
                              maxSize={10 * 1024 * 1024} // 10MB
                              maxFiles={1}
                              uploadType="image"
                              compact={true}
                              autoUpload={true}
                              onUploadComplete={(files) => {
                                if (files.length > 0) {
                                  const updatedScreenshots = [...formData.screenshots];
                                  updatedScreenshots[index] = {
                                    ...screenshot,
                                    url: files[0].url,
                                    name: files[0].filename,
                                    fileId: files[0].id
                                  };
                                  handleChange({
                                    target: {
                                      name: 'screenshots',
                                      value: updatedScreenshots
                                    }
                                  });
                                }
                              }}
                              onUploadError={(errors) => {
                                console.error('Screenshot upload errors:', errors);
                              }}
                              className="w-full"
                            />
                          </div>
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={screenshot.url || screenshot}
                          onChange={(e) => {
                            const updatedScreenshots = [...formData.screenshots];
                            updatedScreenshots[index] = {
                              ...screenshot,
                              url: e.target.value,
                              type: 'url'
                            };
                            handleChange({
                              target: {
                                name: 'screenshots',
                                value: updatedScreenshots
                              }
                            });
                          }}
                          placeholder="https://example.com/screenshot.jpg"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const updatedScreenshots = [...formData.screenshots];
                          updatedScreenshots[index] = {
                            ...screenshot,
                            type: screenshot.type === 'upload' ? 'url' : 'upload'
                          };
                          handleChange({
                            target: {
                              name: 'screenshots',
                              value: updatedScreenshots
                            }
                          });
                        }}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-400"
                      >
                        {screenshot.type === 'upload' ? 'Switch to URL' : 'Switch to Upload'}
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => {
                          const updatedScreenshots = formData.screenshots.filter((_, i) => i !== index);
                          handleChange({
                            target: {
                              name: 'screenshots',
                              value: updatedScreenshots
                            }
                          });
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )) : (
                  <div className="text-sm text-gray-500">No screenshots added</div>
                )}
                
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const updatedScreenshots = [...(Array.isArray(formData.screenshots) ? formData.screenshots : []), { url: '', type: 'url' }];
                      handleChange({
                        target: {
                          name: 'screenshots',
                          value: updatedScreenshots
                        }
                      });
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add URL
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const updatedScreenshots = [...(Array.isArray(formData.screenshots) ? formData.screenshots : []), { url: '', type: 'upload' }];
                      handleChange({
                        target: {
                          name: 'screenshots',
                          value: updatedScreenshots
                        }
                      });
                    }}
                    className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Upload Image
                  </button>
                </div>
                            </div>
            </div>
          </div>

          {/* Tags Section */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 sm:p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-600" />
              Tags & Keywords
            </h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tags/Keywords
              </label>
              <div className="space-y-2">
                {Array.isArray(formData.tags) ? formData.tags.map((tag, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={tag}
                      onChange={(e) => {
                        const updatedTags = [...formData.tags];
                        updatedTags[index] = e.target.value;
                        handleChange({
                          target: {
                            name: 'tags',
                            value: updatedTags
                          }
                        });
                      }}
                      placeholder="Enter tag..."
                      className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const updatedTags = formData.tags.filter((_, i) => i !== index);
                        handleChange({
                          target: {
                            name: 'tags',
                            value: updatedTags
                          }
                        });
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )) : (
                  <div className="text-sm text-gray-500">No tags added</div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const updatedTags = [...(Array.isArray(formData.tags) ? formData.tags : []), ''];
                    handleChange({
                      target: {
                        name: 'tags',
                        value: updatedTags
                      }
                    });
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Tag
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Add keywords or tags to help users find this product</p>
            </div>
          </div>
          
          {/* License Section */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 sm:p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              License Information
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Regular License Points
                </label>
                <div className="space-y-2">
                  {Array.isArray(formData.regular_license_points) ? formData.regular_license_points.map((point, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={point}
                        onChange={(e) => {
                          const updatedPoints = [...formData.regular_license_points];
                          updatedPoints[index] = e.target.value;
                          handleChange({
                            target: {
                              name: 'regular_license_points',
                              value: updatedPoints
                            }
                          });
                        }}
                        placeholder="Enter point..."
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updatedPoints = formData.regular_license_points.filter((_, i) => i !== index);
                          handleChange({
                            target: {
                              name: 'regular_license_points',
                              value: updatedPoints
                            }
                          });
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )) : (
                    <div className="text-sm text-gray-500">No points added</div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const updatedPoints = [...(Array.isArray(formData.regular_license_points) ? formData.regular_license_points : []), ''];
                      handleChange({
                        target: {
                          name: 'regular_license_points',
                          value: updatedPoints
                        }
                      });
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Point
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Extended License Points
                </label>
                <div className="space-y-2">
                  {Array.isArray(formData.extended_license_points) ? formData.extended_license_points.map((point, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={point}
                        onChange={(e) => {
                          const updatedPoints = [...formData.extended_license_points];
                          updatedPoints[index] = e.target.value;
                          handleChange({
                            target: {
                              name: 'extended_license_points',
                              value: updatedPoints
                            }
                          });
                        }}
                        placeholder="Enter point..."
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updatedPoints = formData.extended_license_points.filter((_, i) => i !== index);
                          handleChange({
                            target: {
                              name: 'extended_license_points',
                              value: updatedPoints
                            }
                          });
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )) : (
                    <div className="text-sm text-gray-500">No points added</div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const updatedPoints = [...(Array.isArray(formData.extended_license_points) ? formData.extended_license_points : []), ''];
                      handleChange({
                        target: {
                          name: 'extended_license_points',
                          value: updatedPoints
                        }
                      });
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Point
                  </button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Regular License Price
              </label>
              <input
                type="number"
                  name="regular_license_price"
                  value={formData.regular_license_price}
                onChange={handleChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Regular Original Price
              </label>
              <input
                type="number"
                  name="regular_license_original_price"
                  value={formData.regular_license_original_price}
                onChange={handleChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Regular Discount (%)
              </label>
              <input
                type="number"
                  name="regular_license_discount"
                  value={formData.regular_license_discount}
                onChange={handleChange}
                min="0"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Extended License Price
              </label>
              <input
                  type="number"
                  name="extended_license_price"
                  value={formData.extended_license_price}
                onChange={handleChange}
                  step="0.01"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Extended Original Price
              </label>
              <input
                  type="number"
                  name="extended_license_original_price"
                  value={formData.extended_license_original_price}
                onChange={handleChange}
                  step="0.01"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Extended Discount (%)
              </label>
              <input
                  type="number"
                  name="extended_license_discount"
                  value={formData.extended_license_discount}
                onChange={handleChange}
                  min="0"
                  max="100"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              </div>
            </div>
            </div>
            
          {/* Extended Support Section */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 sm:p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-600" />
              Extended Support
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Support Text
                </label>
                <textarea
                  name="extended_support_text"
                  value={formData.extended_support_text}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Enter support description..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Support Price
              </label>
              <input
                  type="number"
                  name="extended_support_price"
                  value={formData.extended_support_price}
                onChange={handleChange}
                  step="0.01"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Support Original Price
                </label>
                <input
                  type="number"
                  name="extended_support_original_price"
                  value={formData.extended_support_original_price}
                  onChange={handleChange}
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Support Discount (%)
                </label>
                <input
                  type="number"
                  name="extended_support_discount"
                  value={formData.extended_support_discount}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>
          
          {/* Addons Section */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 sm:p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-600" />
              Addons
            </h4>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Product Addons</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const newAddon = {
                        id: Date.now(),
                        name: '',
                        short_description: '',
                        price: '',
                        original_price: '',
                        discount_percentage: '',
                        type: 'custom'
                      };
                      handleChange({
                        target: {
                          name: 'addons',
                          value: [...formData.addons, newAddon]
                        }
                      });
                    }}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    + Custom Addon
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenProductSelector}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    + Select Product
                  </button>
                </div>
              </div>
              
              {formData.addons.map((addon, index) => (
                <div key={addon.id || index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Addon {index + 1}
                      </span>
                      {addon.type === 'product' && (
                        <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded">
                          Product
                        </span>
                      )}
                      {addon.type === 'custom' && (
                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                          Custom
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updatedAddons = formData.addons.filter((_, i) => i !== index);
                        handleChange({
                          target: {
                            name: 'addons',
                            value: updatedAddons
                          }
                        });
                      }}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Addon Name
                      </label>
                      <input
                        type="text"
                        value={addon.name}
                        onChange={(e) => {
                          const updatedAddons = [...formData.addons];
                          updatedAddons[index] = { ...addon, name: e.target.value };
                          handleChange({
                            target: {
                              name: 'addons',
                              value: updatedAddons
                            }
                          });
                        }}
                        placeholder="Custom Design"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Short Description
                      </label>
                      <input
                        type="text"
                        value={addon.short_description}
                        onChange={(e) => {
                          const updatedAddons = [...formData.addons];
                          updatedAddons[index] = { ...addon, short_description: e.target.value };
                          handleChange({
                            target: {
                              name: 'addons',
                              value: updatedAddons
                            }
                          });
                        }}
                        placeholder="Custom design modifications"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Price
                      </label>
                      <input
                        type="number"
                        value={addon.price}
                        onChange={(e) => {
                          const updatedAddons = [...formData.addons];
                          updatedAddons[index] = { ...addon, price: e.target.value };
                          handleChange({
                            target: {
                              name: 'addons',
                              value: updatedAddons
                            }
                          });
                        }}
                        step="0.01"
                        placeholder="29.99"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Original Price
                      </label>
                      <input
                        type="number"
                        value={addon.original_price}
                        onChange={(e) => {
                          const updatedAddons = [...formData.addons];
                          updatedAddons[index] = { ...addon, original_price: e.target.value };
                          handleChange({
                            target: {
                              name: 'addons',
                              value: updatedAddons
                            }
                          });
                        }}
                        step="0.01"
                        placeholder="49.99"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Discount (%)
                      </label>
                      <input
                        type="number"
                        value={addon.discount_percentage}
                        onChange={(e) => {
                          const updatedAddons = [...formData.addons];
                          updatedAddons[index] = { ...addon, discount_percentage: e.target.value };
                          handleChange({
                            target: {
                              name: 'addons',
                              value: updatedAddons
                            }
                          });
                        }}
                        min="0"
                        max="100"
                        placeholder="40"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
          
          {/* Additional Information */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 sm:p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-orange-600" />
              Additional Information
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Date
                </label>
                <input
                  type="date"
                  name="last_date"
                  value={formData.last_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Included
                </label>
                <textarea
                  name="included"
                  value={formData.included}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Enter what's included..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="coming_soon">Coming Soon</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                Featured Product
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="disable_full_payment_coupon"
                checked={formData.disable_full_payment_coupon}
                onChange={handleChange}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                Disable Full Payment Coupon for this Product
              </label>
            </div>
          </div>
          
        </form>
        </div>
      </div>

      {/* Product Selector Modal */}
      {showProductSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Select Product as Addon
                </h3>
                <button
                  onClick={() => setShowProductSelector(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              {/* Products List */}
              <div className="max-h-96 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableProducts
                    .filter(p => 
                      p.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
                      p.category.toLowerCase().includes(productSearchTerm.toLowerCase())
                    )
                    .map((availableProduct) => (
                      <div
                        key={availableProduct.id}
                        className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        onClick={() => {
                          // Add the selected product as an addon
                          const newAddon = {
                            id: availableProduct.id,
                            name: availableProduct.name,
                            short_description: availableProduct.description || '',
                            price: availableProduct.regular_license_price || 0,
                            original_price: availableProduct.regular_license_original_price || 0,
                            discount_percentage: availableProduct.regular_license_discount || 0,
                            type: 'product',
                            product_id: availableProduct.id,
                            product_slug: availableProduct.slug
                          };
                          
                          handleChange({
                            target: {
                              name: 'addons',
                              value: [...formData.addons, newAddon]
                            }
                          });
                          
                          setShowProductSelector(false);
                          setProductSearchTerm('');
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                            <Package className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                              {availableProduct.name}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {getCategoryDisplayName(availableProduct.category, productCategories)}
                            </p>
                            <p className="text-xs font-medium text-blue-600 dark:text-blue-400">
                              ₹{availableProduct.regular_license_price || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
                
                {availableProducts.filter(p => 
                  p.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
                  p.category.toLowerCase().includes(productSearchTerm.toLowerCase())
                ).length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No products found
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Default Values Modal Component
const DefaultValuesModal = ({ isOpen, onClose, defaultValues, setDefaultValues, onSave, loading }) => {
  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setDefaultValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setDefaultValues(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field) => {
    setDefaultValues(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setDefaultValues(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Product Default Values
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-8">
            {/* Rating and Sales Section */}
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-600" />
                Rating & Sales Information
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Product Rating
                  </label>
                  <input
                    type="number"
                    value={defaultValues.rating}
                    onChange={(e) => handleChange('rating', parseFloat(e.target.value) || 0)}
                    min="0"
                    max="5"
                    step="0.1"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Number of Reviews
                  </label>
                  <input
                    type="number"
                    value={defaultValues.reviews_count}
                    onChange={(e) => handleChange('reviews_count', parseInt(e.target.value) || 0)}
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Number of Sales
                  </label>
                  <input
                    type="number"
                    value={defaultValues.sales_count}
                    onChange={(e) => handleChange('sales_count', parseInt(e.target.value) || 0)}
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Regular License Section */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                Regular License Defaults
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    License Points
                  </label>
                  <div className="space-y-2">
                    {defaultValues.regular_license_points.map((point, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={point}
                          onChange={(e) => handleArrayChange('regular_license_points', index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="Enter license point"
                        />
                        <button
                          type="button"
                          onClick={() => removeArrayItem('regular_license_points', index)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addArrayItem('regular_license_points')}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      + Add License Point
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Regular Price
                    </label>
                    <input
                      type="number"
                      value={defaultValues.regular_license_price}
                      onChange={(e) => handleChange('regular_license_price', e.target.value)}
                      step="0.01"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Original Price
                    </label>
                    <input
                      type="number"
                      value={defaultValues.regular_license_original_price}
                      onChange={(e) => handleChange('regular_license_original_price', e.target.value)}
                      step="0.01"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      value={defaultValues.regular_license_discount}
                      onChange={(e) => handleChange('regular_license_discount', parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Extended License Section */}
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-600" />
                Extended License Defaults
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    License Points
                  </label>
                  <div className="space-y-2">
                    {defaultValues.extended_license_points.map((point, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={point}
                          onChange={(e) => handleArrayChange('extended_license_points', index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="Enter license point"
                        />
                        <button
                          type="button"
                          onClick={() => removeArrayItem('extended_license_points', index)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addArrayItem('extended_license_points')}
                      className="text-green-600 hover:text-green-800 text-sm font-medium"
                    >
                      + Add License Point
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Extended Price
                    </label>
                    <input
                      type="number"
                      value={defaultValues.extended_license_price}
                      onChange={(e) => handleChange('extended_license_price', e.target.value)}
                      step="0.01"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Original Price
                    </label>
                    <input
                      type="number"
                      value={defaultValues.extended_license_original_price}
                      onChange={(e) => handleChange('extended_license_original_price', e.target.value)}
                      step="0.01"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      value={defaultValues.extended_license_discount}
                      onChange={(e) => handleChange('extended_license_discount', parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Support Section */}
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-purple-600" />
                Extended Support Defaults
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Support Message/Text
                  </label>
                  <textarea
                    value={defaultValues.extended_support_text}
                    onChange={(e) => handleChange('extended_support_text', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="Enter support message or text"
                  />
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Support Price
                    </label>
                    <input
                      type="number"
                      value={defaultValues.extended_support_price}
                      onChange={(e) => handleChange('extended_support_price', e.target.value)}
                      step="0.01"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Original Price
                    </label>
                    <input
                      type="number"
                      value={defaultValues.extended_support_original_price}
                      onChange={(e) => handleChange('extended_support_original_price', e.target.value)}
                      step="0.01"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      value={defaultValues.extended_support_discount}
                      onChange={(e) => handleChange('extended_support_discount', parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Fields Section */}
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-600" />
                Additional Information Defaults
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Last Date
                  </label>
                  <input
                    type="date"
                    value={defaultValues.last_date}
                    onChange={(e) => handleChange('last_date', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Included Content
                  </label>
                  <textarea
                    value={defaultValues.included}
                    onChange={(e) => handleChange('included', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="Enter included content details"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Modal Actions */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={loading}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Default Values
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsManagement;
